import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
// @ts-ignore - App is still JavaScript
import App from "@/App";
import { installChunkErrorRecovery } from "./utils/lazyWithRetry";

// Iter310 — production self-heal: a stale service worker aborting a lazy
// chunk used to leave the user staring at the three-dot boot screen forever.
installChunkErrorRecovery();

/* ── Service Worker registration + self-heal ────────────────────────
 * Production black-screen bug 2026-06-27: when a new deploy ships,
 * users with the OLD service worker get their browser to request
 * `static/js/main.<oldhash>.js` — which the new build no longer
 * serves, so the page goes pitch black.
 *
 * Two safeties below:
 *   (a) When the SW is updated, immediately reload once to pick up
 *       the new JS bundle hashes referenced in the freshly-fetched
 *       index.html.
 *   (b) If React lazy() ever hits a "ChunkLoadError" / "Loading
 *       chunk failed" — usually mid-session right after a deploy —
 *       unregister the SW and hard-reload so the user is back on
 *       the latest assets.
 * Both safeties only fire ONCE per page-load via sessionStorage.
 */
if ('serviceWorker' in navigator) {
  // Capture the BEFORE state so we know whether the upcoming
  // `controllerchange` event represents (1) a brand-new install on a
  // never-controlled tab — no reload needed — or (2) a genuine
  // update from an existing controller — reload to pick up new
  // chunks.
  const hadControllerAtStart = !!navigator.serviceWorker.controller;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(
      (reg) => {
        console.log('[App] Service worker registered, scope:', reg.scope);
        // Poll for SW updates every 30 min — covers long-running PWA sessions.
        setInterval(() => { reg.update().catch(() => {}); }, 30 * 60 * 1000);
      },
      (err) => console.warn('[App] Service worker registration failed:', err),
    );

    // (a) When the controlling SW changes (= new SW activated), reload —
    //     but only if there was already a controller AND this document was
    //     itself served from the SW cache. Navigations are network-first, so
    //     a document that came over the network already references the new
    //     bundle hashes; reloading it just gave users a visible 1-2 s blank
    //     re-load on every deploy for nothing. Chunk 404s from a genuinely
    //     stale (cache-served) document are still covered by (b) below.
    let reloadGuard = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      const htmlFromNetwork = !!nav && nav.transferSize > 0;
      if (reloadGuard || !hadControllerAtStart || htmlFromNetwork) return;
      reloadGuard = true;
      console.log('[App] SW controller changed on a cache-served document — reloading for fresh assets');
      window.location.reload();
    });
  });
}

// (b) Catch chunk-load failures (stale main bundle referencing chunks
//     that no longer exist post-deploy). Unregister SW + hard reload.
const SELF_HEAL_KEY = '__chunk_self_heal_done__';
function selfHealOnChunkError(event: PromiseRejectionEvent | ErrorEvent) {
  const reason =
    (event as PromiseRejectionEvent).reason || (event as ErrorEvent).error || (event as ErrorEvent).message || '';
  const msg = (reason && (reason.message || reason)) + '';
  if (
    msg &&
    (msg.includes('ChunkLoadError') ||
      msg.includes('Loading chunk') ||
      msg.includes('Loading CSS chunk'))
  ) {
    // Only attempt this once per session — avoids reload loops if the
    // server is actually returning 404s for chunks for some other reason.
    if (sessionStorage.getItem(SELF_HEAL_KEY) === '1') return;
    sessionStorage.setItem(SELF_HEAL_KEY, '1');
    console.warn('[App] Chunk load failed — clearing SW + caches and reloading');
    (async () => {
      try {
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
        }
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
      } finally {
        window.location.reload();
      }
    })();
  }
}
window.addEventListener('unhandledrejection', selfHealOnChunkError);
window.addEventListener('error', selfHealOnChunkError);

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

/* Iter312 — production-only "page loads forever, only 3 dots" on the
 * prerendered SEO entry points (/exercises.html, /trainers.html, …).
 *
 * Those files ship static crawler markup inside `#root`, so the container
 * has several pre-existing children. React's own container clear did NOT
 * remove them there, which left `#fm-boot` — a `position:fixed; inset:0;
 * z-index:9` OPAQUE splash — painted on top of the fully mounted app
 * forever. Dev/preview never hit it (CRA serves public/index.html, no
 * prerendered children), hence "works in preview, broken in production".
 *
 * Fix: snapshot the prerendered nodes and delete them as soon as React has
 * appended its own tree.
 */
const prerenderedNodes = Array.from(rootElement.children);

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

let stripTries = 0;
const stripPrerenderedShell = () => {
  const mounted = Array.from(rootElement.children).some((n) => !prerenderedNodes.includes(n));
  if (mounted) {
    prerenderedNodes.forEach((n) => n.remove());
    // Safety net: on prerendered pages the splash could end up OUTSIDE #root,
    // where React can never clear it.
    document.getElementById('fm-boot')?.remove();
    return;
  }
  stripTries += 1;
  if (stripTries < 600) requestAnimationFrame(stripPrerenderedShell);
};
requestAnimationFrame(stripPrerenderedShell);
